// apps/admin/src/pages/billing/PaymentRequestsPage.tsx
//
// Phase 8.2 Wave 4 Task 26 — Admin Pre-payment Requests page.
//
// vg-binding: CONTEXT:decisions=P8.D-236,P8.D-237,P8.D-239,P8.D-241,P8.D-242,P8.D-243
// vg-binding: PHASE:08.2-admin-finance-catalog
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Route: /admin/billing/payment-requests (Finance sidebar entry).
//
//  list columns: PR#, Merchant, Amount, Date, Legal Entity
// Created By. Search by merchant name / PR number + date range filter.
// Row click opens <PaymentRequestPdfModal> for preview + download.
// No edit / cancel / duplicate (immutable).
//
// Permission gate: CREATE_PAYMENT_REQUEST. UI hides the "Create"
// button when the user lacks the permission; the BE also enforces 403 on
// POST so this is purely a UX guard.
//
// Endpoints consumed:
//   GET  /api/v1/admin/payment-requests?page=&pageSize=&search=&from=&to=
//   POST /api/v1/admin/payment-requests (Idempotency-Key — delegated to
//        <PaymentRequestCreateForm>)
//   GET  /api/v1/admin/payment-requests/:id (resolves pdf_url for modal)
//   GET  /api/v1/admin/payment-requests/:id/pdf (signed URL — modal href)

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import {
  envelopeError,
  unwrap,
  type LooseClient,
} from './billing-shared.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { useHasPermission } from '../../stores/useAuthStore.js';
import { PaymentRequestCreateForm } from '../../features/billing/PaymentRequestCreateForm.js';
import { PaymentRequestPdfModal } from '../../features/billing/PaymentRequestPdfModal.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of BE list shape.
// ---------------------------------------------------------------------------

interface PaymentRequestRow {
  id: string;
  pr_number: string;
  merchant_id: string;
  merchant_name: string | null;
  amount_usd_cents: number;
  legal_entity_id: string;
  legal_entity_name: string | null;
  pdf_url: string | null;
  created_by: string | null;
  created_by_name?: string | null;
  created_at: string;
}

interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface ListResponse {
  payment_requests: PaymentRequestRow[];
  pagination: PaginationMeta;
}

// Phase 8.2 audit 2026-05-23 — DEFAULT_PAGE_SIZE retained for legacy refs
// but the live value now flows from the URL-synced `pageSize` searchParam
// so the Rows-per-page selector in PageListFooter survives a refresh.
const DEFAULT_PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatUsdCents(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PaymentRequestsPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  // dbg-20260725 (#2632) — reactive hook, NOT the `s.hasPermission` store
  // selector: that selects a stable method identity, so the component never
  // re-renders when hydrate() resolves permissions on a hard page load.
  const hasPermission = useHasPermission();
  const canCreate = hasPermission(PERMISSIONS.CREATE_PAYMENT_REQUEST);

  const [searchParams, setSearchParams] = useSearchParams();

  // URL-synced filter state — keeps the back-button useful + the page
  // shareable across admins (search + date range).
  const search = searchParams.get('search') ?? '';
  const fromDate = searchParams.get('from') ?? '';
  const toDate = searchParams.get('to') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1'));
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE
  // pageSize cap (1..100).
  const rawPageSize = Number(searchParams.get('pageSize') ?? '25');
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;

  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  const updateParam = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // --- Data state ---
  const [rows, setRows] = useState<PaymentRequestRow[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Modals ---
  const [createOpen, setCreateOpen] = useState(false);
  const [pdfTarget, setPdfTarget] = useState<{
    prNumber: string;
    pdfUrl: string | null;
  } | null>(null);

  const queryString = useMemo(() => {
    const qp = new URLSearchParams();
    qp.set('page', String(page));
    qp.set('pageSize', String(pageSize));
    if (search) qp.set('search', search);
    if (fromDate) qp.set('from', fromDate);
    if (toDate) qp.set('to', toDate);
    return qp.toString();
  }, [page, pageSize, search, fromDate, toDate]);

  const fetchList = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data, response } = await client.GET(
        `/api/v1/admin/payment-requests?${queryString}`,
      );
      if (!response.ok) {
        setError(envelopeError(data) || `HTTP ${response.status}`);
        setRows([]);
        return;
      }
      const body = unwrap<ListResponse | undefined>(data) ?? {
        payment_requests: [],
        pagination: { page, pageSize, total: 0, totalPages: 0 },
      };
      setRows(Array.isArray(body.payment_requests) ? body.payment_requests : []);
      setPagination(
        body.pagination ?? { page, pageSize, total: 0, totalPages: 0 },
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('payment_request.networkError'),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryString, page, pageSize, t]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openPdf = useCallback(
    (row: PaymentRequestRow): void => {
      // pdf_url may be null while async render in flight. We pass
      // null through so the modal can render the empty-state placeholder.
      setPdfTarget({ prNumber: row.pr_number, pdfUrl: row.pdf_url });
    },
    [],
  );

  const onCreated = useCallback(
    (payload: { id: string; pr_number: string }) => {
      // Refresh the list to surface the new PR on top + jump page 1 so
      // the user can see their just-created doc immediately.
      updateParam({ page: '1' });
      void fetchList();
      toast({
        title: t('payment_request.toast.created'),
        description: t('payment_request.toast.createdDesc', {
          prNumber: payload.pr_number,
        }),
        variant: 'success',
      });
    },
    [fetchList, toast, updateParam, t],
  );

  // --- Render ---
  return (
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-6">
      <PageHeader
        title={t('payment_request.title')}
        subtitle={t('payment_request.page.description')}
      />

      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          {/* Filters bar — search + date range */}
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              updateParam({ search: searchDraft || null, page: '1' });
            }}
          >
            <label className="flex flex-col text-xs text-pw-text-muted">
              <span className="mb-1 font-medium">
                {t('payment_request.filters.search')}
              </span>
              <Input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder={t('payment_request.filters.searchPlaceholder')}
                className="w-72"
                data-testid="payment-request-search-input"
              />
            </label>
            <label className="flex flex-col text-xs text-pw-text-muted">
              <span className="mb-1 font-medium">{t('payment_request.filters.from')}</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) =>
                  updateParam({ from: e.target.value || null, page: '1' })
                }
                data-testid="payment-request-from-input"
              />
            </label>
            <label className="flex flex-col text-xs text-pw-text-muted">
              <span className="mb-1 font-medium">{t('payment_request.filters.to')}</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) =>
                  updateParam({ to: e.target.value || null, page: '1' })
                }
                data-testid="payment-request-to-input"
              />
            </label>
            <Button type="submit" variant="secondary">
              {t('payment_request.filters.searchBtn')}
            </Button>
            <div className="ml-auto">
              {canCreate ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setCreateOpen(true)}
                  data-testid="payment-request-create-btn"
                >
                  + {t('payment_request.actions.create')}
                </Button>
              ) : (
                <span
                  className="text-xs text-pw-text-muted"
                  title={t('payment_request.noPermTitle')}
                >
                  {t('payment_request.noPerm')}
                </span>
              )}
            </div>
          </form>

          {/* List table — columns */}
          <div className="overflow-x-auto rounded-md border border-pw-border">
            <table className="min-w-full text-sm">
              <thead className="bg-pw-surface text-xs uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">{t('payment_request.columns.number')}</th>
                  <th className="px-3 py-2 text-left">{t('payment_request.columns.merchant')}</th>
                  <th className="px-3 py-2 text-right">{t('payment_request.columns.amount')}</th>
                  <th className="px-3 py-2 text-left">{t('payment_request.columns.date')}</th>
                  <th className="px-3 py-2 text-left">{t('payment_request.columns.legalEntity')}</th>
                  <th className="px-3 py-2 text-left">{t('payment_request.columns.createdBy')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-xs text-pw-text-muted"
                    >
                      {t('payment_request.loading')}
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-xs text-red-700 dark:text-red-300"
                      data-testid="payment-request-list-error"
                    >
                      {error}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-xs text-pw-text-muted"
                      data-testid="payment-request-list-empty"
                    >
                      {t('payment_request.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'cursor-pointer border-t border-pw-border transition-colors hover:bg-pw-surface/40',
                      )}
                      onClick={() => openPdf(row)}
                      data-testid={`payment-request-row-${row.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.pr_number}
                      </td>
                      {/* dbg-20260725-pr-list-columns: never fall back to the
                          raw ObjectId — it tells the accountant nothing. The BE
                          resolves the merchant OWNER's email into
                          `merchant_name`; if even that is missing, an em dash is
                          more honest than a 24-char hex id. */}
                      <td className="px-3 py-2">{row.merchant_name || '—'}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatUsdCents(row.amount_usd_cents)}
                      </td>
                      <td className="px-3 py-2">
                        {formatDateOnly(row.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        {row.legal_entity_name || row.legal_entity_id}
                      </td>
                      <td className="px-3 py-2 text-pw-text-muted">
                        {/* Same reasoning as the Merchant cell — `created_by`
                            is an ObjectId, not something to show a human. */}
                        {row.created_by_name || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          <PageListFooter
            page={pagination.page}
            pageSize={pageSize}
            total={pagination.total}
            totalPages={pagination.totalPages}
            loading={loading}
            onPageChange={(next) => updateParam({ page: String(next) })}
            onPageSizeChange={(size) =>
              updateParam({ pageSize: String(size), page: '1' })
            }
            testId="payment-requests-footer"
          />
        </CardContent>
      </Card>

      {/* Create form — gated on permission so users without it never see it */}
      {canCreate ? (
        <PaymentRequestCreateForm
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={onCreated}
        />
      ) : null}

      {/* PDF preview modal — preview + download */}
      <PaymentRequestPdfModal
        open={Boolean(pdfTarget)}
        prNumber={pdfTarget?.prNumber ?? null}
        pdfUrl={pdfTarget?.pdfUrl ?? null}
        onClose={() => setPdfTarget(null)}
      />
    </main>
  );
}

export default PaymentRequestsPage;
